import {
  GetStockProductModel,
  StockProductItem,
} from "features/stock-ordering/core/domain/get-stock-product.model";
import React, { useState, useEffect, useMemo } from "react";
import { LuPackage, LuSearch, LuX, LuCheck } from "react-icons/lu";
import { MdAttachMoney } from "react-icons/md";
import { useDelayedEmptyState } from "../components/stock-ordering-utils";
import { StockOrderedEditProductState } from "../slices/stock-order-edit-product.slice";

interface ProductSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedProducts: StockProductItem[]) => void;
  products: StockProductItem[];
  title?: string;
  subtitle?: string;
  multiSelect?: boolean;
  maxSelection?: number;
  preSelectedProducts?: StockProductItem[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  withQuantity?: boolean;
}

export function AddProductSelectModal(props: ProductSelectModalProps) {
  const {
    isOpen,
    onClose,
    onConfirm,
    products,
    title = "Select Products",
    subtitle = "Choose products to add to your order",
    multiSelect = true,
    maxSelection,
    searchPlaceholder = "Search products...",
    preSelectedProducts,
    withQuantity = false,
  } = props;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedProducts, setSelectedProducts] = useState<StockProductItem[]>(
    preSelectedProducts ?? []
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  // useEffect(() => {
  //   if (withQuantity === false) {
  //     onConfirm(selectedProducts);
  //   }
  // }, [selectedProducts]);

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    const seen = new Set<string>();

    return products
      .filter((product) => {
        const matchesSearch =
          product.product_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          product.product_id.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
      })
      .filter((product) => {
        if (seen.has(product.product_id)) return false;
        seen.add(product.product_id);
        return true;
      });
  }, [products, searchTerm]);

  const { showEmpty, isWaiting } = useDelayedEmptyState(
    filteredProducts.length !== 0,
    30
  );

  // Check if product is selected
  const isProductSelected = (productId: string): boolean => {
    return selectedProducts.some((p) => p.product_id === productId);
  };

  // Get selected product quantity
  const getSelectedQuantity = (productId: string): number => {
    const selected = selectedProducts.find((p) => p.product_id === productId);
    return selected?.orderQty ?? 0;
  };

  // Handle product selection
  const handleProductSelect = (product: StockProductItem) => {
    if (!multiSelect) {
      // Single select mode
      const newSelection: StockProductItem = {
        ...product,
        orderQty: 0,
      };
      setSelectedProducts([newSelection]);

      return;
    }

    // Multi select mode
    const isSelected = isProductSelected(product.product_id);

    if (isSelected) {
      // Remove from selection
      setSelectedProducts((prev) =>
        prev.filter((p) => p.product_id !== product.product_id)
      );
    } else {
      // Add to selection (check max limit)
      if (maxSelection && selectedProducts.length >= maxSelection) {
        alert(`You can only select up to ${maxSelection} products.`);
        return;
      }

      const newSelection: StockProductItem = {
        ...product,
        orderQty: withQuantity ? 0 : 1,
      };
      setSelectedProducts((prev) => [...prev, newSelection]);
    }
  };

  // Handle quantity change
  const handleQuantityChange = (productId: string, quantity: number) => {
    // Allow 0 for empty input, but don't allow negative
    if (quantity < 0) return;

    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.product_id === productId ? { ...p, orderQty: quantity } : p
      )
    );
  };

  // Handle quantity input change
  const handleQuantityInputChange = (productId: string, value: string) => {
    // Allow empty string for user to clear and type new value
    if (value === "") {
      setSelectedProducts((prev) =>
        prev.map((p) =>
          p.product_id === productId ? { ...p, orderQty: 0 } : p
        )
      );
      return;
    }

    // Parse the input value
    const numValue = parseInt(value, 10);

    // Validate the input - allow any positive number
    if (!isNaN(numValue) && numValue >= 0) {
      handleQuantityChange(productId, numValue);
    }
  };

  // Handle confirm - only include products with quantity > 0
  const handleConfirm = () => {
    const productsWithQuantity = selectedProducts.filter(
      (product) => product.orderQty > 0
    );
    onConfirm(withQuantity ? productsWithQuantity : selectedProducts);
    onClose();
  };

  // Calculate total - only include products with quantity > 0
  const totalAmount = selectedProducts.reduce(
    (sum, product) =>
      sum + product.cost * (product.orderQty > 0 ? product.orderQty : 0),
    0
  );

  // Count products with actual quantity > 0
  const productsWithQuantity = selectedProducts.filter(
    (product) => product.orderQty > 0
  ).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex -top-5 items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <LuX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <LuSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isWaiting ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading products...</span>
            </div>
          ) : showEmpty ? (
            <div className="text-center py-12">
              <LuPackage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {searchTerm
                  ? "No products found matching your criteria"
                  : "No products available. Contact MIS for product enrollment."}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredProducts.map((product) => {
                const isSelected = isProductSelected(product.product_id);
                const quantity = getSelectedQuantity(product.product_id);

                return (
                  <div
                    key={product.product_id}
                    className={`border rounded-xl p-4 transition-all cursor-pointer ${
                      isSelected
                        ? "border-blue-500 bg-blue-50 shadow-md"
                        : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                    }`}
                    onClick={() => handleProductSelect(product)}
                  >
                    <div className="flex items-center space-x-4">
                      {/* Selection Indicator */}
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected && (
                          <LuCheck className="w-3 h-3 text-white" />
                        )}
                      </div>

                      {/* Product Image */}
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center flex-shrink-0">
                        <LuPackage className="w-6 h-6 text-gray-500" />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                            {product.product_id}
                          </span>
                          {product.active === 1 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                              Inactive
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1 leading-tight">
                          {product.product_name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>UOM: {product.uom}</span>
                          <span>Stock: {product.currentStock}</span>
                          <span>Order Qty: {product.orderQty}</span>
                        </div>
                      </div>

                      {/* Price and Quantity */}
                      <div className="flex flex-col items-end space-y-2">
                        <div className="flex items-center space-x-1">
                          <MdAttachMoney className="w-4 h-4 text-gray-600" />
                          <span className="font-bold text-gray-900">
                            ₱{Number(product.cost).toFixed(2)}
                          </span>
                        </div>

                        {withQuantity && isSelected && multiSelect && (
                          <div
                            className="flex items-center space-x-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() =>
                                handleQuantityChange(
                                  product.product_id,
                                  quantity - 1
                                )
                              }
                              className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-300 transition-colors"
                              disabled={quantity <= 0}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="0"
                              value={quantity === 0 ? "" : quantity}
                              onChange={(e) =>
                                handleQuantityInputChange(
                                  product.product_id,
                                  e.target.value
                                )
                              }
                              placeholder="0"
                              className="w-16 text-center font-medium border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <button
                              onClick={() =>
                                handleQuantityChange(
                                  product.product_id,
                                  quantity + 1
                                )
                              }
                              className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-300 transition-colors"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">
                {withQuantity ? productsWithQuantity : selectedProducts.length}{" "}
                product
                {(withQuantity
                  ? productsWithQuantity
                  : selectedProducts.length) !== 1
                  ? "s"
                  : ""}{" "}
                selected
                {maxSelection && ` (max ${maxSelection})`}
              </p>
              {(withQuantity
                ? productsWithQuantity > 0
                : selectedProducts.length > 0) && (
                <p className="text-lg font-bold text-gray-900">
                  Total: ₱{totalAmount.toFixed(2)}
                </p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleConfirm}
                disabled={
                  withQuantity
                    ? productsWithQuantity === 0
                    : selectedProducts.length === 0
                }
                className="hidden lg:block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Confirm Selection
              </button>

              <button
                onClick={handleConfirm}
                disabled={
                  withQuantity
                    ? productsWithQuantity === 0
                    : selectedProducts.length === 0
                }
                className="lg:hidden px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
